import { Model } from 'pinia-orm';
import { Str, Num, Bool, Uid, Attr } from 'pinia-orm/decorators';
import type { ApiChildChild } from 'src/types/contentTypes';
import type { StrapiAttr } from 'src/types/strapi';

/** Scalar field types are derived from the backend schema — see src/types/strapi.ts. */
type ChildAttr<K extends keyof ApiChildChild['attributes']> = StrapiAttr<
  ApiChildChild,
  K
>;

export type License = {
  id: number;
  documentId: string;
  assigned_at: string;
  valid_until: string;
};

export type PublicSchoolCity = {
  id: number;
  documentId?: string;
  city: string;
};

export type PublicSchool = {
  id: number;
  documentId: string;
  short_name?: string;
  full_name?: string;
  city?: PublicSchoolCity | null;
};

export class Child extends Model {
  static override entity = 'children';

  @Uid() declare id: number;
  @Str('') declare documentId: string;
  // firstname/lastname live on the linked child_user, not on the child itself,
  // so they have no attribute on ApiChildChild to derive from.
  @Str('') declare firstname: string;
  @Str('') declare lastname: string;
  @Str('') declare school: ChildAttr<'school'>;
  @Num(0) declare class: ChildAttr<'class'>;
  @Bool(false) declare is_active: ChildAttr<'is_active'>;

  @Attr(null) declare user_id: number | null;
  @Attr(null) declare license: License[] | null;
  // Source of truth for school + city; the legacy `school` string is a fallback.
  @Attr(null) declare public_school: PublicSchool | null;

  // Computed full name (not persisted)
  get fullname(): string {
    const fn = this.firstname?.trim();
    const ln = this.lastname?.trim();
    return [fn, ln].filter(Boolean).join(' ');
  }

  // Display name derived from the public-school relation, falling back to the
  // legacy free-text `school` string for records created before the migration.
  get schoolName(): string {
    return (
      this.public_school?.short_name ||
      this.public_school?.full_name ||
      this.school ||
      ''
    );
  }

  // City derived from the public-school relation.
  get cityName(): string {
    return this.public_school?.city?.city || '';
  }

  get validLicense(): boolean {
    const license = this.mostRecentLicense;
    if (!license || !license.valid_until) return false;

    const now = new Date();
    const validUntil = new Date(license.valid_until);

    return validUntil >= now;
  }

  get mostRecentLicense(): License | null {
    if (!this.license || this.license.length === 0) return null;

    // Sort licenses by valid_until date (most recent first)
    const sortedLicenses = this.license.sort(
      (a, b) =>
        new Date(b.valid_until).getTime() - new Date(a.valid_until).getTime(),
    );

    return sortedLicenses[0] || null;
  }
}
